import { RT4K_OPT_PAD, rt4kArrayElementOffset, rt4kFieldOffset, type Rt4kStructFieldName } from './layout';
import type { ByteRange } from './schema';

export type OptimalModeGroup = '240p' | '480i' | '288p' | '576i';

export interface OptimalModeDefinition {
  slot: number;
  label: string;
  group: OptimalModeGroup;
  decimationFactor: number | null;
  implemented: boolean;
  reserved: boolean;
}

export interface OptimalModeResolution {
  width: number;
  height: number;
}

const SLOT_ENABLED_PATH = 'advanced.acquisition.sample_rate_detection.optimal_sampling.slot_enabled';

const OPTIMAL_MODE_SETTING_FIELDS = new Map<string, Rt4kStructFieldName>([
  [SLOT_ENABLED_PATH, 'opt_dec_factor_en'],
  ['advanced.scaling.input_crop.right_trim', 'opt_scaler_trim_right'],
  ['advanced.scaling.input_crop.left_trim', 'opt_scaler_trim_left'],
  ['advanced.scaling.input_crop.top_trim', 'opt_scaler_trim_top'],
  ['advanced.scaling.input_crop.bottom_trim', 'opt_scaler_trim_bottom'],
  ['advanced.scaling.scaler.aspect_correction', 'opt_scaler_ar_mode'],
  ['advanced.scaling.scaler.scaling_mode', 'opt_scaler_mode'],
  ['advanced.acquisition.rgb_adc.sampling.decimation_phase', 'opt_tvp_dec_phase'],
  ['advanced.acquisition.rgb_adc.sampling.sub_phase', 'opt_tvp_adc_phase'],
]);

const SLOT_DATA_FIELDS: Rt4kStructFieldName[] = [
  'opt_dec_factor_en',
  'opt_tvp_adc_phase',
  'opt_tvp_dec_phase',
  'opt_scaler_trim_right',
  'opt_scaler_trim_left',
  'opt_scaler_trim_top',
  'opt_scaler_trim_bottom',
  'opt_scaler_ar_mode',
  'opt_scaler_mode',
  'opt_scaler_vert_ar_factor',
  'opt_scaler_hori_free_factor',
  'opt_scaler_vert_free_factor',
];

const DECIMATION_FACTORS = [10, 8, 7, 6, 5, 4] as const;
const GROUPS = [
  { group: '240p' as const, start: 0, height: 240 },
  { group: '480i' as const, start: 8, height: 480 },
  { group: '288p' as const, start: 16, height: 288 },
  { group: '576i' as const, start: 24, height: 576 },
];
const BASE_INPUT_SLOT_BY_GROUP = new Map<OptimalModeGroup, number>([
  ['240p', 2],
  ['480i', 1],
  ['288p', 4],
  ['576i', 3],
]);

export const OPTIMAL_MODE_DEFINITIONS: OptimalModeDefinition[] = GROUPS.flatMap(({ group, start }) => [
  ...DECIMATION_FACTORS.map((factor, index) => ({
    slot: start + index,
    label: `${group} Dec 1/${factor}`,
    group,
    decimationFactor: factor,
    implemented: true,
    reserved: false,
  })),
  ...[6, 7].map((offset) => ({
    slot: start + offset,
    label: `${group} Reserved`,
    group,
    decimationFactor: null,
    implemented: false,
    reserved: true,
  })),
]);

const OPTIMAL_MODE_DEFINITIONS_BY_SLOT = new Map(OPTIMAL_MODE_DEFINITIONS.map((definition) => [definition.slot, definition]));
const OPTIMAL_MODE_RESOLUTIONS = new Map<number, OptimalModeResolution>(
  GROUPS.flatMap(({ group, start, height }) =>
    Array.from({ length: 8 }, (_, index) => [
      start + index,
      { width: 1440, height, group },
    ] as const),
  ).map(([slot, resolution]) => [slot, { width: resolution.width, height: resolution.height }]),
);

export function optimalModeDefinitionForSlot(slot: number): OptimalModeDefinition {
  return OPTIMAL_MODE_DEFINITIONS_BY_SLOT.get(slot) ?? {
    slot,
    label: `Optimal Sampling Mode ${slot}`,
    group: '240p',
    decimationFactor: null,
    implemented: false,
    reserved: true,
  };
}

export function optimalModeOptionLabel(slot: number): string {
  const definition = optimalModeDefinitionForSlot(slot);
  return `${definition.slot}: ${definition.label}`;
}

export function optimalModeDecimationFactorForSlot(slot: number | null | undefined): number | null {
  if (slot === null || slot === undefined) {
    return null;
  }

  return optimalModeDefinitionForSlot(slot).decimationFactor;
}

export function optimalModeNominalResolutionForSlot(slot: number | null | undefined): OptimalModeResolution | null {
  if (slot === null || slot === undefined) {
    return null;
  }

  return OPTIMAL_MODE_RESOLUTIONS.get(slot) ?? null;
}

export function optimalModeBaseInputSlotForSlot(slot: number | null | undefined): number | null {
  if (slot === null || slot === undefined) {
    return null;
  }

  return BASE_INPUT_SLOT_BY_GROUP.get(optimalModeDefinitionForSlot(slot).group) ?? null;
}

export function optimalModeDefaultAspectCorrection(slot: number | null | undefined): string | null {
  return slot === null || slot === undefined ? null : '4:3 (PAR)';
}

export function optimalSampleDetectionEnabled(bytes: Uint8Array): boolean {
  const address = rt4kFieldOffset('opt_sample_det_en');
  if (address + 4 > bytes.length) {
    return false;
  }

  return new DataView(bytes.buffer, bytes.byteOffset + address, 4).getInt32(0, true) !== 0;
}

export function optimalModeByteRangesForSettingPath(path: string, slot: number | null | undefined): ByteRange[] | null {
  if (slot === null || slot === undefined) {
    return null;
  }

  const fieldName = OPTIMAL_MODE_SETTING_FIELDS.get(path);
  if (!fieldName || slot < 0 || slot >= RT4K_OPT_PAD) {
    return null;
  }

  return [
    {
      address: rt4kArrayElementOffset(fieldName, slot),
      length: optimalModeFieldElementLength(fieldName),
    },
  ];
}

export function isOptimalModeSlotSettingPath(path: string): boolean {
  return OPTIMAL_MODE_SETTING_FIELDS.has(path);
}

export function populatedOptimalModeDefinitions(bytes: Uint8Array): OptimalModeDefinition[] {
  return OPTIMAL_MODE_DEFINITIONS.filter((definition) => optimalModeSlotHasData(bytes, definition.slot));
}

export function optimalModeDataByteRanges(slot: number): ByteRange[] {
  return SLOT_DATA_FIELDS.map((fieldName) => ({
    address: rt4kArrayElementOffset(fieldName, slot),
    length: optimalModeFieldElementLength(fieldName),
  }));
}

export function optimalModeSlotHasData(bytes: Uint8Array, slot: number): boolean {
  if (slot < 0 || slot >= RT4K_OPT_PAD) {
    return false;
  }

  return SLOT_DATA_FIELDS.some((fieldName) => optimalModeFieldHasData(bytes, fieldName, slot));
}

function optimalModeFieldHasData(bytes: Uint8Array, fieldName: Rt4kStructFieldName, slot: number): boolean {
  const address = rt4kArrayElementOffset(fieldName, slot);
  const length = optimalModeFieldElementLength(fieldName);
  if (address + length > bytes.length) {
    return false;
  }

  if (length === 2) {
    return new DataView(bytes.buffer, bytes.byteOffset + address, 2).getInt16(0, true) !== 0;
  }

  return (bytes[address] ?? 0) !== 0;
}

function optimalModeFieldElementLength(fieldName: Rt4kStructFieldName): 1 | 2 {
  switch (fieldName) {
    case 'opt_scaler_trim_right':
    case 'opt_scaler_trim_left':
    case 'opt_scaler_trim_top':
    case 'opt_scaler_trim_bottom':
    case 'opt_scaler_vert_ar_factor':
    case 'opt_scaler_hori_free_factor':
    case 'opt_scaler_vert_free_factor':
      return 2;
    default:
      return 1;
  }
}
